/**
 * FloatingSpeedDial — Botón flotante unificado que agrupa todas las acciones rápidas.
 *
 * Al hacer click en el FAB principal se despliegan sub-botones con efecto escalonado.
 * Usa inline styles porque se renderiza fuera del Frame de Polaris y necesita
 * position:fixed real contra el viewport.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureFlags } from '@smarty/shared';

// ─── Config ──────────────────────────────────────────────────────────────────

const FAB_SIZE = 52;
const CHILD_SIZE = 44;
const GAP = 14;
// El copiloto ocupa el hero abajo-derecha (bottom:24); el speed-dial se apila encima.
const BOTTOM = 88;
const RIGHT = 24;

/** Rutas donde el speed dial se oculta completamente */
const HIDDEN_ON_ROUTES = [
  '/chats/inbox',
  '/apps/crm/inbox',
  '/llamadas/dialer',
];

// ─── Child action definitions ────────────────────────────────────────────────

export interface SpeedDialAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  hoverColor: string;
  onClick: () => void;
  /** Indicador de actividad (punto pulsante) */
  pulse?: boolean;
  /** Acción visible solo si esta función retorna true */
  visible?: () => boolean;
}

// ─── Keyframes (una sola vez) ────────────────────────────────────────────────

let injected = false;
function injectStyles() {
  if (injected) return;
  injected = true;
  const el = document.createElement('style');
  el.textContent = `
    @keyframes sf-sd-pop {
      0% { transform: scale(0) translateY(10px); opacity: 0; }
      70% { transform: scale(1.08) translateY(0); opacity: 1; }
      100% { transform: scale(1) translateY(0); opacity: 1; }
    }
    @keyframes sf-sd-hide {
      0% { transform: scale(1) translateY(0); opacity: 1; }
      100% { transform: scale(0) translateY(10px); opacity: 0; }
    }
    @keyframes sf-sd-pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.6); opacity: 0; }
    }
    @keyframes sf-sd-rotate-in {
      from { transform: rotate(0deg); }
      to { transform: rotate(135deg); }
    }
    @keyframes sf-sd-rotate-out {
      from { transform: rotate(135deg); }
      to { transform: rotate(0deg); }
    }
  `;
  document.head.appendChild(el);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  /** Acciones extra a mostrar además de las predeterminadas. */
  extraActions?: SpeedDialAction[];
}

export function FloatingSpeedDial({
  extraActions = [],
}: Props = {}) {
  const { pathname } = useLocation();
  const { isFeatureEnabled, planType } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { injectStyles(); }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClose = useCallback(() => {
    setAnimatingOut(true);
    setTimeout(() => {
      setOpen(false);
      setAnimatingOut(false);
    }, 200);
  }, []);

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      setOpen(true);
      setAnimatingOut(false);
    }
  }, [open, handleClose]);

  // Cerrar al navegar
  useEffect(() => {
    if (open) handleClose();
  }, [pathname]);

  const hidden = HIDDEN_ON_ROUTES.some((r) => pathname.includes(r));
  if (hidden) return null;

  // ─── Build actions list ──────────────────────────────────────────────────

  const actions: SpeedDialAction[] = [];

  // El AI Copilot ya NO va acá: tiene su propio botón flotante dedicado y gateado
  // (CopilotFab en AppLayout → CopilotDrawer → crm-copilot, solo plan con feature 'copilot').

  /*
   * Teléfono — sólo si el plan trae call center.
   *
   * 🚨 Antes se ofrecía SIEMPRE. Un revendedor de Drop —cuyo plan es logística, operaciones y
   *    avisos— veía un botón de teléfono que no lleva a ningún lado: la peor clase de botón, el que
   *    promete una función que la cuenta no tiene. Un menú de acciones rápidas tiene que ofrecer lo
   *    que la cuenta PUEDE hacer, no el catálogo completo del producto.
   */
  if (isFeatureEnabled('call_center')) actions.push({
    id: 'dialer',
    label: 'Teléfono',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
        <path d="M3.62 7.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V17c0 .55-.45 1-1 1C7.61 18 0 10.39 0 1c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
      </svg>
    ),
    color: '#16A34A',
    hoverColor: '#15803D',
    onClick: () => {
      handleClose();
      window.dispatchEvent(new CustomEvent('sf:open-dialer'));
    },
  });

  // Sugerir una mejora — ADR-0017. Va acá y no en un botón propio: el panel ya
  // tiene suficientes elementos flotantes, y este es el lugar donde el usuario
  // ya busca acciones que no pertenecen a la pantalla en la que está.
  /*
   * 🪤 Todavía no para el plan `drop`. Decisión de producto: el revendedor entra a operar sus
   *    ventas, no a opinar sobre el panel del que sólo ve un pedazo. Se usa `planType` —el mismo
   *    predicado que la base— y no «no tiene ecommerce», para no tener dos definiciones de lo
   *    mismo. Ver el comentario de `planType` en FeatureFlagsContext.
   */
  if (planType !== 'drop') actions.push({
    id: 'sugerir-mejora',
    label: 'Sugerir mejora',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2zm-2 18a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1h-4v1z" />
      </svg>
    ),
    color: '#E2551A',
    hoverColor: '#C2450F',
    onClick: () => {
      handleClose();
      window.dispatchEvent(new CustomEvent('sf:sugerir-mejora'));
    },
  });

  // Acciones extras
  for (const a of extraActions) {
    if (a.visible && !a.visible()) continue;
    actions.push(a);
  }

  /*
   * ⭐ SIN ACCIONES, NO HAY BOTÓN. Un «+» que se abre y no muestra nada es peor que no estar: el
   *   usuario lo pulsa, no pasa nada y concluye que la aplicación está rota. Esto es lo que saca el
   *   botón de la pantalla del revendedor sin necesidad de una regla que lo nombre.
   */
  if (actions.length === 0) return null;

  const isExpanded = open && !animatingOut;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} style={{ position: 'fixed', bottom: BOTTOM, right: RIGHT, zIndex: 99990 }}>
      {/* Tooltips/labels */}
      {open && actions.map((action, i) => {
        const offsetY = (i + 1) * (CHILD_SIZE + GAP);
        return (
          <div
            key={`label-${action.id}`}
            style={{
              position: 'absolute',
              bottom: offsetY + (CHILD_SIZE - 24) / 2,
              right: CHILD_SIZE + 12,
              background: 'rgba(0,0,0,0.8)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 500,
              padding: '4px 10px',
              borderRadius: 6,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              animation: animatingOut
                ? `sf-sd-hide 0.2s ease forwards`
                : `sf-sd-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 50}ms both`,
            }}
          >
            {action.label}
          </div>
        );
      })}

      {/* Child action buttons */}
      {open && actions.map((action, i) => {
        const offsetY = (i + 1) * (CHILD_SIZE + GAP);
        return (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            title={action.label}
            onClick={action.onClick}
            style={{
              position: 'absolute',
              bottom: offsetY,
              right: (FAB_SIZE - CHILD_SIZE) / 2,
              width: CHILD_SIZE,
              height: CHILD_SIZE,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              background: action.color,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              animation: animatingOut
                ? `sf-sd-hide 0.15s ease ${(actions.length - 1 - i) * 30}ms forwards`
                : `sf-sd-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 50}ms both`,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = action.hoverColor; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = action.color; }}
          >
            {action.icon}
            {/* Pulso de actividad */}
            {action.pulse && (
              <span style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#FBBF24',
                border: '2px solid #fff',
              }}>
                <span style={{
                  position: 'absolute',
                  inset: -2,
                  borderRadius: '50%',
                  background: '#FBBF24',
                  animation: 'sf-sd-pulse 1.5s ease-in-out infinite',
                }} />
              </span>
            )}
          </button>
        );
      })}

      {/* FAB principal */}
      <button
        type="button"
        aria-label={open ? 'Cerrar menú rápido' : 'Abrir menú rápido'}
        aria-expanded={open}
        onClick={handleToggle}
        style={{
          position: 'relative',
          width: FAB_SIZE,
          height: FAB_SIZE,
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          background: isExpanded
            ? '#374151'
            : 'linear-gradient(135deg, #2563EB, #7C3AED)',
          boxShadow: isExpanded
            ? '0 4px 16px rgba(0,0,0,0.25)'
            : '0 4px 16px rgba(37,99,235,0.4)',
          transition: 'background 0.3s, box-shadow 0.3s',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transform: isExpanded ? 'rotate(135deg)' : 'rotate(0deg)',
          }}
        >
          <line x1="11" y1="4" x2="11" y2="18" />
          <line x1="4" y1="11" x2="18" y2="11" />
        </svg>
      </button>

      {/* Backdrop sutil cuando está abierto */}
      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: -1,
            background: 'rgba(0,0,0,0.08)',
            transition: 'opacity 0.2s',
            opacity: animatingOut ? 0 : 1,
          }}
          onClick={handleClose}
          aria-hidden
        />
      )}
    </div>
  );
}
